import type { 
  ValidationSchema, 
  ValidationRule, 
  ValidationResult, 
  ValidationError,
  BusinessRule
} from '../types';


import type { 
  PlanogramTemplate,
  MilestoneConfig,
  CycleConfig,
  ScopeConfig
} from '../types/plan-template';

// Note: Since this is a utility file, we cannot use the useTranslation hook directly.
// We will need to pass the translation function as a parameter or use a different approach.
// For now, we'll prepare the structure to support internationalization.
/**
 * Validation utility functions for planogram template management
 */

/**
 * Validate a single field against a validation rule
 */
export function validateField(
  value: any, 
  rule: ValidationRule, 
  allData?: any,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationError | null {
  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback to original messages if no translation function provided
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.fieldRequired': '此字段为必填项',
      'planTemplate.validation.invalidDateFormat': '日期格式不正确',
      'planTemplate.validation.invalidFieldType': `字段类型应为${params?.type || ''}`,
      'planTemplate.validation.minLength': `最少需要${params?.minLength || 0}个字符`,
      'planTemplate.validation.maxLength': `最多允许${params?.maxLength || 0}个字符`,
      'planTemplate.validation.minValue': `值不能小于${params?.min !== undefined ? params.min : ''}`,
      'planTemplate.validation.maxValue': `值不能大于${params?.max !== undefined ? params.max : ''}`,
      'planTemplate.validation.invalidFormat': '格式不正确'
    };
    return fallbackMessages[key] || rule.message || '验证失败';
  };

  // Check required
  if (rule.required && (value === undefined || value === null || value === '')) {
    return {
      field: rule.field,
      message: rule.message || getTranslatedMessage('planTemplate.validation.fieldRequired')
    };
  }

  // Skip validation if value is empty and not required
  if (!rule.required && (value === undefined || value === null || value === '')) {
    return null;
  }

  // Type validation
  if (rule.type) {
    const actualType = Array.isArray(value) ? 'array' : typeof value;
    if (rule.type === 'date') {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return {
          field: rule.field,
          message: rule.message || getTranslatedMessage('planTemplate.validation.invalidDateFormat')
        };
      }
    } else if (actualType !== rule.type) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.invalidFieldType', { type: rule.type })
      };
    }
  }

  // String length validation
  if (rule.type === 'string' && typeof value === 'string') {
    if (rule.minLength && value.length < rule.minLength) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.minLength', { minLength: rule.minLength })
      };
    }
    if (rule.maxLength && value.length > rule.maxLength) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.maxLength', { maxLength: rule.maxLength })
      };
    }
  }

  // Number range validation
  if (rule.type === 'number' && typeof value === 'number') {
    if (rule.min !== undefined && value < rule.min) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.minValue', { min: rule.min })
      };
    }
    if (rule.max !== undefined && value > rule.max) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.maxValue', { max: rule.max })
      };
    }
  }

  // Pattern validation
  if (rule.pattern && typeof value === 'string') {
    if (!rule.pattern.test(value)) {
      return {
        field: rule.field,
        message: rule.message || getTranslatedMessage('planTemplate.validation.invalidFormat')
      };
    }
  }

  // Custom validation
  if (rule.custom) {
    const customResult = rule.custom(value, allData);
    if (customResult) {
      return {
        field: rule.field,
        message: customResult
      };
    }
  }

  return null;
}

/**
 * Validate data against a validation schema
 */
export function validateSchema(
  data: any, 
  schema: ValidationSchema,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    if (key === 'planTemplate.validation.dependentFieldRequired') {
      return `当设置了${params?.dependentField || ''}时，${params?.requiredField || ''}为必填项`;
    }
    return '验证失败';
  };

  // Validate each rule
  for (const rule of schema.rules) {
    const fieldValue = getNestedValue(data, rule.field);
    const error = validateField(fieldValue, rule, data, t);
    if (error) {
      errors.push(error);
    }
  }

  // Check dependencies
  if (schema.dependencies) {
    for (const [dependentField, requiredFields] of Object.entries(schema.dependencies)) {
      const dependentValue = getNestedValue(data, dependentField);
      if (dependentValue !== undefined && dependentValue !== null && dependentValue !== '') {
        for (const requiredField of requiredFields) {
          const requiredValue = getNestedValue(data, requiredField);
          if (requiredValue === undefined || requiredValue === null || requiredValue === '') {
            errors.push({
              field: requiredField,
              message: getTranslatedMessage('planTemplate.validation.dependentFieldRequired', { 
                dependentField, 
                requiredField 
              })
            });
          }
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate business rules
 */
export function validateBusinessRules(
  data: any, 
  rules: BusinessRule[],
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    if (key === 'planTemplate.validation.businessRuleFailed') {
      return `业务规则验证失败: ${params?.name || ''}`;
    }
    return '业务规则验证失败';
  };

  for (const rule of rules) {
    try {
      const result = rule.validator(data);
      if (!result.isValid && result.message) {
        const error: ValidationError = {
          field: rule.id,
          message: result.message,
          code: rule.id
        };

        if (rule.severity === 'error') {
          errors.push(error);
        } else if (rule.severity === 'warning') {
          warnings.push(error);
        }
      }
    } catch (error) {
      console.error(`Business rule validation error for ${rule.id}:`, error);
      errors.push({
        field: rule.id,
        message: getTranslatedMessage('planTemplate.validation.businessRuleFailed', { name: rule.name }),
        code: rule.id
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Validate template code uniqueness (mock implementation)
 */
export async function validateTemplateCodeUniqueness(
  code: string, 
  excludeId?: string
): Promise<boolean> {
  // Mock implementation - in real app this would call the API
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Simulate some existing codes
  const existingCodes = ['TPL001', 'TPL002', 'TPL003', 'MAJOR001', 'REG001'];
  
  if (excludeId) {
    // If we're editing an existing template, we need to check if the code
    // belongs to a different template
    return !existingCodes.includes(code) || code === `TPL${excludeId.slice(-3)}`;
  }
  
  return !existingCodes.includes(code);
}

/**
 * Validate milestone sequence uniqueness and continuity
 */
export function validateMilestoneSequence(
  milestones: MilestoneConfig[],
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];
  
  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.milestoneSequenceDuplicate': '里程碑序号不能重复',
      'planTemplate.validation.milestoneSequenceNotContinuous': '里程碑序号必须从1开始连续',
      'planTemplate.validation.lastMilestoneInvalid': '最后一个里程碑必须是"Store Task Generation"'
    };
    return fallbackMessages[key] || '验证失败';
  };
  
  if (milestones.length === 0) {
    return { isValid: true, errors: [] };
  }

  const sequences = milestones.map(m => m.sequence).sort((a, b) => a - b);
  
  // Check for duplicates
  const uniqueSequences = [...new Set(sequences)];
  if (sequences.length !== uniqueSequences.length) {
    errors.push({
      field: 'milestones',
      message: getTranslatedMessage('planTemplate.validation.milestoneSequenceDuplicate')
    });
  }

  // Check for continuity (should start from 1 and be continuous)
  for (let i = 0; i < sequences.length; i++) {
    if (sequences[i] !== i + 1) {
      errors.push({
        field: 'milestones',
        message: getTranslatedMessage('planTemplate.validation.milestoneSequenceNotContinuous')
      });
      break;
    }
  }

  // Check final milestone
  const sortedMilestones = [...milestones].sort((a, b) => a.sequence - b.sequence);
  const lastMilestone = sortedMilestones[sortedMilestones.length - 1];
  if (!lastMilestone?.isFinalMilestone || lastMilestone.name !== 'Store Task Generation') {
    errors.push({
      field: 'milestones',
      message: getTranslatedMessage('planTemplate.validation.lastMilestoneInvalid')
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate cycle configuration
 */
export function validateCycleConfiguration(
  cycle: CycleConfig,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.weeklyFrequencyRequired': '周频率必须选择至少一个工作日',
      'planTemplate.validation.monthlyFrequencyRequired': '月频率必须选择至少一个日期',
      'planTemplate.validation.endDateRequired': '请选择结束日期',
      'planTemplate.validation.endDateBeforeFirstExecution': '结束日期必须晚于首次执行日期',
      'planTemplate.validation.repeatCountInvalid': '重复次数必须大于0',
      'planTemplate.validation.firstExecutionDateInPast': '首次执行日期不能早于当前日期'
    };
    return fallbackMessages[key] || '验证失败';
  };

  // Validate frequency-specific requirements
  if (cycle.frequencyType === 'Weekly') {
    if (!cycle.weekDaySelection || cycle.weekDaySelection.length === 0) {
      errors.push({
        field: 'weekDaySelection',
        message: getTranslatedMessage('planTemplate.validation.weeklyFrequencyRequired')
      });
    }
  }

  if (cycle.frequencyType === 'Monthly') {
    if (!cycle.monthDaySelection || cycle.monthDaySelection.length === 0) {
      errors.push({
        field: 'monthDaySelection',
        message: getTranslatedMessage('planTemplate.validation.monthlyFrequencyRequired')
      });
    }
  }

  // Validate termination configuration
  if (cycle.terminationType === 'End on Date') {
    if (!cycle.endDate) {
      errors.push({
        field: 'endDate',
        message: getTranslatedMessage('planTemplate.validation.endDateRequired')
      });
    } else {
      const endDate = new Date(cycle.endDate);
      const startDate = new Date(cycle.firstExecutionDate);
      if (endDate <= startDate) {
        errors.push({
          field: 'endDate',
          message: getTranslatedMessage('planTemplate.validation.endDateBeforeFirstExecution')
        });
      }
    }
  }

  if (cycle.terminationType === 'Limited Count') {
    if (!cycle.repeatCount || cycle.repeatCount < 1) {
      errors.push({
        field: 'repeatCount',
        message: getTranslatedMessage('planTemplate.validation.repeatCountInvalid')
      });
    }
  }

  // Validate first execution date
  const firstExecution = new Date(cycle.firstExecutionDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
  
  if (firstExecution < now) {
    errors.push({
      field: 'firstExecutionDate',
      message: getTranslatedMessage('planTemplate.validation.firstExecutionDateInPast')
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate scope configuration
 */
export function validateScopeConfiguration(
  scope: ScopeConfig,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.storeGroupRequired': '至少选择一个门店组',
      'planTemplate.validation.storeRequired': '至少选择一个门店',
      'planTemplate.validation.storeSelectionLimitExceeded': '单次门店选择数量不能超过1000',
      'planTemplate.validation.filterConditionRequired': '请至少设置一个筛选条件',
      'planTemplate.validation.storeCountInvalid': '影响范围必须包含至少一个门店'
    };
    return fallbackMessages[key] || '验证失败';
  };

  // Validate scope type specific requirements
  switch (scope.scopeType) {
    case 'Specific Store Groups':
      if (!scope.storeGroupList || scope.storeGroupList.length === 0) {
        errors.push({
          field: 'storeGroupList',
          message: getTranslatedMessage('planTemplate.validation.storeGroupRequired')
        });
      }
      break;
    
    case 'Individual Stores':
      if (!scope.customStoreList || scope.customStoreList.length === 0) {
        errors.push({
          field: 'customStoreList',
          message: getTranslatedMessage('planTemplate.validation.storeRequired')
        });
      } else if (scope.customStoreList.length > 1000) {
        errors.push({
          field: 'customStoreList',
          message: getTranslatedMessage('planTemplate.validation.storeSelectionLimitExceeded')
        });
      }
      break;
    
    case 'Custom Store Collections':
      if (!scope.storeSelectionCriteria || 
          (!scope.storeSelectionCriteria.areas?.length && 
           !scope.storeSelectionCriteria.formats?.length && 
           !scope.storeSelectionCriteria.formats?.length)) {
        errors.push({
          field: 'storeSelectionCriteria',
          message: getTranslatedMessage('planTemplate.validation.filterConditionRequired')
        });
      }
      break;
  }

  // Validate store count
  if (scope.storeCount <= 0) {
    errors.push({
      field: 'storeCount',
      message: getTranslatedMessage('planTemplate.validation.storeCountInvalid')
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Comprehensive template validation
 */
export function validateTemplate(
  template: Partial<PlanogramTemplate>,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const allErrors: ValidationError[] = [];
  const allWarnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.codeRequired': 'Code为必填项',
      'planTemplate.validation.codeInvalid': '模板代码必须为3-20位字符，且包含大写字母和数字',
      'planTemplate.validation.nameRequired': '名称为必填项',
      'planTemplate.validation.nameInvalid': '名称必须为2-50个字符',
      'planTemplate.validation.planTypeRequired': '计划类型为必填项',
      'planTemplate.validation.descriptionRequired': '描述为必填项',
      'planTemplate.validation.descriptionInvalid': '描述必须为10-500个字符',
      'planTemplate.validation.milestonesRequired': '里程碑为必填项',
      'planTemplate.validation.executionCycleRequired': '执行周期为必填项',
      'planTemplate.validation.scopeDefinitionRequired': '范围定义为必填项',
      'planTemplate.validation.totalDurationExceedsYear': `总时长${params?.totalDuration || 0}天超过一年`
    };
    return fallbackMessages[key] || '验证失败';
  };

  // Basic field validation

  if (!template.name) {
    allErrors.push({ field: 'name', message: getTranslatedMessage('planTemplate.validation.nameRequired'), type: 'error' });
  } else if (template.name.length < 2 || template.name.length > 50) {
    allErrors.push({ field: 'name', message: getTranslatedMessage('planTemplate.validation.nameInvalid'), type: 'error' });
  }

  if (!template.planType) {
    allErrors.push({ field: 'planType', message: getTranslatedMessage('planTemplate.validation.planTypeRequired'), type: 'error' });
  }

  if (!template.description) {
    allErrors.push({ field: 'description', message: getTranslatedMessage('planTemplate.validation.descriptionRequired'), type: 'error' });
  } else if (template.description.length < 10 || template.description.length > 500) {
    allErrors.push({ field: 'description', message: getTranslatedMessage('planTemplate.validation.descriptionInvalid'), type: 'error' });
  }

  // if (!template.businessActivity) {
  //   allErrors.push({ field: 'businessActivity', message: getTranslatedMessage('planTemplate.validation.businessActivityRequired'), type: 'error' });
  // }

  // Validate milestones if present
  if (template.milestones) {
    const milestoneResult = validateMilestoneSequence(template.milestones, t);
    allErrors.push(...milestoneResult.errors);
    if (milestoneResult.warnings) {
      allWarnings.push(...milestoneResult.warnings);
    }
  } else {
    allErrors.push({ field: 'milestones', message: getTranslatedMessage('planTemplate.validation.milestonesRequired'), type: 'error' });
  }

  // Validate cycle if present
  if (template.executionCycle) {
    const cycleResult = validateCycleConfiguration(template.executionCycle, t);
    allErrors.push(...cycleResult.errors);
    if (cycleResult.warnings) {
      allWarnings.push(...cycleResult.warnings);
    }
  } else {
    allErrors.push({ field: 'executionCycle', message: getTranslatedMessage('planTemplate.validation.executionCycleRequired'), type: 'error' });
  }

  // Validate scope if present
  if (template.scopeDefinition) {
    const scopeResult = validateScopeConfiguration(template.scopeDefinition, t);
    allErrors.push(...scopeResult.errors);
    if (scopeResult.warnings) {
      allWarnings.push(...scopeResult.warnings);
    }
  } else {
    allErrors.push({ field: 'scopeDefinition', message: getTranslatedMessage('planTemplate.validation.scopeDefinitionRequired'), type: 'error' });
  }

  // Template-level business rules
  if (template.milestones && template.milestones.length > 0) {
    const totalDuration = template.milestones.reduce((sum, m) => 
      sum + m.durationEstimate + m.bufferTime, 0
    );
    if (totalDuration > 365) {
      allWarnings.push({ 
        field: 'milestones', 
        message: getTranslatedMessage('planTemplate.validation.totalDurationExceedsYear', { totalDuration }), 
        type: 'warning' 
      });
    }
  }

  // Check plan type and milestone compatibility
  if (template.planType && template.milestones) {
    const planTypeValidation = validatePlanTypeMilestoneCompatibility(
      template.planType, 
      template.milestones
    );
    allWarnings.push(...planTypeValidation.warnings || []);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings
  };
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: ValidationError[]): string[] {
  return errors.map(error => {
    if (error.field && error.message) {
      return `${error.field}: ${error.message}`;
    }
    return error.message;
  });
}

/**
 * Check if field has validation error
 */
export function hasFieldError(errors: ValidationError[], fieldName: string): boolean {
  return errors.some(error => error.field === fieldName);
}

/**
 * Get validation error message for specific field
 */
export function getFieldError(errors: ValidationError[], fieldName: string): string | undefined {
  const error = errors.find(error => error.field === fieldName);
  return error?.message;
}

/**
 * Validate plan type and milestone compatibility
 */
export function validatePlanTypeMilestoneCompatibility(
  planType: string,
  milestones: MilestoneConfig[],
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const warnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    return 'Validate Failed';
  };

  // Check milestone count based on plan type
  const expectedCounts: Record<string, { min: number; max: number }> = {
    'Major Plans': { min: 7, max: 9 },
    'Regular Plans': { min: 4, max: 6 },
    'Minor Plans': { min: 3, max: 4 },
    'Urgent Plans': { min: 3, max: 4 },
    'Special Plans': { min: 5, max: 7 }
  };

  const expected = expectedCounts[planType];
  if (expected) {
    if (milestones.length < expected.min) {
      warnings.push({
        field: 'milestones',
        message: getTranslatedMessage('planTemplate.wizard.scopeConfiguration.validation.minStoreCount', { 
          planType, 
          minCount: expected.min 
        })
      });
    } else if (milestones.length > expected.max) {
      warnings.push({
        field: 'milestones',
        message: getTranslatedMessage('planTemplate.wizard.scopeConfiguration.validation.maxStoreCount', { planType, maxCount: expected.max })
      });
    }
  }

  // Check duration expectations
  const totalDuration = milestones.reduce((sum, m) => sum + m.durationEstimate, 0);
  const expectedDurations: Record<string, { min: number; max: number }> = {
    'Major Plans': { min: 20, max: 60 },
    'Regular Plans': { min: 7, max: 21 },
    'Minor Plans': { min: 3, max: 10 },
    'Urgent Plans': { min: 1, max: 5 },
    'Special Plans': { min: 10, max: 30 }
  };

  const expectedDuration = expectedDurations[planType];
  if (expectedDuration) {
    if (totalDuration < expectedDuration.min) {
      warnings.push({
        field: 'milestones',
        message: getTranslatedMessage('planTemplate.validation.durationTooShort', { 
          planType, 
          totalDuration, 
          min: expectedDuration.min, 
          max: expectedDuration.max 
        })
      });
    } else if (totalDuration > expectedDuration.max) {
      warnings.push({
        field: 'milestones',
        message: getTranslatedMessage('planTemplate.validation.durationTooLong', { 
          planType, 
          totalDuration, 
          min: expectedDuration.min, 
          max: expectedDuration.max 
        })
      });
    }
  }

  return {
    isValid: true,
    errors: [],
    warnings
  };
}

/**
 * Validate template configuration completeness
 */
export function validateTemplateCompleteness(
  template: Partial<PlanogramTemplate>,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.codeRequired': 'Code为必填项',
      'planTemplate.validation.nameRequired': '名称为必填项',
      'planTemplate.validation.planTypeRequired': '计划类型为必填项',
      'planTemplate.validation.descriptionRequired': '描述为必填项',
      'planTemplate.validation.businessActivityRequired': '业务活动为必填项',
      'planTemplate.validation.milestonesRequired': '里程碑为必填项',
      'planTemplate.validation.executionCycleRequired': '执行周期为必填项',
      'planTemplate.validation.scopeDefinitionRequired': '范围定义为必填项',
      'planTemplate.validation.remarkRequired': '备注为必填项'
    };
    return fallbackMessages[key] || `${params?.name || ''}为必填项`;
  };

  // Check required sections
  const requiredSections = [
    { field: 'code', name: '模板代码' },
    { field: 'name', name: '模板名称' },
    { field: 'planType', name: '计划类型' },
    { field: 'description', name: '模板描述' },
    { field: 'businessActivity', name: '业务活动' },
    { field: 'milestones', name: '里程碑配置' },
    { field: 'executionCycle', name: '执行周期' },
    { field: 'scopeDefinition', name: '影响范围' }
  ];

  for (const section of requiredSections) {
    const value = (template as any)[section.field];
    if (!value || (Array.isArray(value) && value.length === 0)) {
      errors.push({
        field: section.field,
        message: getTranslatedMessage(`${section.field}Required`, { name: section.name }), type: 'error' 
      });
    }
  }

  // Check optional but recommended fields
  if (!template.remark) {
    warnings.push({
      field: 'remark',
      message: getTranslatedMessage('planTemplate.validation.remarkRequired'), type: 'error' 
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Validate template for publishing
 */
export function validateTemplateForPublishing(
  template: PlanogramTemplate,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.publishStatusInvalid': '只有草稿状态的模板可以发布',
      'planTemplate.validation.publishValidationFailed': '模板必须通过验证后才能发布',
      'planTemplate.validation.publishNotTested': '建议先测试模板后再发布'
    };
    return fallbackMessages[key] || '验证失败';
  };

  // All basic validations must pass
  const basicValidation = validateTemplate(template, t);
  errors.push(...basicValidation.errors);
  warnings.push(...(basicValidation.warnings || []));

  // Additional publishing requirements
  if (template.status !== 'Draft') {
    errors.push({
      field: 'status',
      message: getTranslatedMessage('planTemplate.validation.publishStatusInvalid'), type: 'error' 
    });
  }

  if (template.validationStatus !== 'Validation Passed') {
    errors.push({
      field: 'validationStatus',
      message: getTranslatedMessage('planTemplate.validation.publishValidationFailed'), type: 'error' 
    });
  }

  // Check if template has been tested
  if (!template.usageStatistics || template.usageStatistics.usageCount === 0) {
    warnings.push({
      field: 'usageStatistics',
      message: getTranslatedMessage('planTemplate.validation.publishNotTested'), type: 'error' 
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Validate batch operations
 */
export function validateBatchOperation(
  templates: PlanogramTemplate[],
  operation: 'enable' | 'disable' | 'delete',
  t?: (key: string, params?: Record<string, any>) => string
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.batchOperationNoTemplates': '请选择要操作的模板',
      'planTemplate.validation.templateWithActivePlans': `${params?.count || 0}个模板存在生效中的计划，无法删除`,
      'planTemplate.validation.noPublishedTemplatesSelected': '所选模板中没有已发布的模板',
      'planTemplate.validation.noDisabledTemplatesSelected': '所选模板中没有已停用的模板'
    };
    return fallbackMessages[key] || '验证失败';
  };

  if (templates.length === 0) {
    errors.push({
      field: 'templates',
      message: getTranslatedMessage('planTemplate.validation.batchOperationNoTemplates'), type: 'error'  
    });
    return { isValid: false, errors, warnings };
  }

  // Operation-specific validations
  switch (operation) {
    case 'delete':
      const templatesWithActivePlans = templates.filter(t => 
        t.usageStatistics.activePlans > 0
      );
      if (templatesWithActivePlans.length > 0) {
        errors.push({
          field: 'templates',
          message: getTranslatedMessage('planTemplate.validation.templateWithActivePlans', { 
            count: templatesWithActivePlans.length 
          }), type: 'error' 
        });
      }
      break;

    case 'disable':
      const publishedTemplates = templates.filter(t => t.status === 'Published');
      if (publishedTemplates.length === 0) {
        warnings.push({
          field: 'templates',
          message: getTranslatedMessage('planTemplate.validation.noPublishedTemplatesSelected'), type: 'error' 
        });
      }
      break;

    case 'enable':
      const disabledTemplates = templates.filter(t => t.status === 'Disabled');
      if (disabledTemplates.length === 0) {
        warnings.push({
          field: 'templates',
          message: getTranslatedMessage('planTemplate.validation.noDisabledTemplatesSelected'), type: 'error' 
        });
      }
      break;


  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Real-time validation for form fields
 */
export function validateFormField(
  fieldName: string,
  value: any,
  t?: (key: string, params?: Record<string, any>) => string
): ValidationError | null {
  // Helper function to get translated message
  const getTranslatedMessage = (key: string, params?: Record<string, any>): string => {
    if (t) {
      return t(key, params);
    }
    // Fallback messages
    const fallbackMessages: Record<string, string> = {
      'planTemplate.validation.templateCodeEmpty': '模板代码不能为空',
      'planTemplate.validation.templateCodeInvalid': '模板代码必须为3-20位字符',
      'planTemplate.validation.templateNameEmpty': '模板名称不能为空',
      'planTemplate.validation.templateNameInvalid': '模板名称必须为2-50字符',
      'planTemplate.validation.templateDescriptionEmpty': '模板描述不能为空',
      'planTemplate.validation.templateDescriptionInvalid': '模板描述必须为10-500字符',
      'planTemplate.validation.milestoneRequired': '请至少配置一个里程碑',
      'planTemplate.validation.milestoneLimitExceeded': '最多支持9个里程碑'
    };
    return fallbackMessages[key] || '验证失败';
  };

  switch (fieldName) {
    case 'code':
      if (!value) return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateCodeEmpty') };
      if (typeof value !== 'string' || value.length < 3 || value.length > 20) {
        return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateCodeInvalid') };
      }
      break;

    case 'name':
      if (!value) return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateNameEmpty') };
      if (value.length < 2 || value.length > 50) {
        return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateNameInvalid') };
      }
      break;

    case 'description':
      if (!value) return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateDescriptionEmpty') };
      if (value.length < 10 || value.length > 500) {
        return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.templateDescriptionInvalid') };
      }
      break;

    case 'milestones':
      if (!Array.isArray(value) || value.length === 0) {
        return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.milestoneRequired') };
      }
      if (value.length > 9) {
        return { field: fieldName, message: getTranslatedMessage('planTemplate.validation.milestoneLimitExceeded') };
      }
      break;

    default:
      return null;
  }

  return null;
}

/**
 * Debounced validation function
 */
export function createDebouncedValidator<T>(
  validator: (data: T) => ValidationResult | Promise<ValidationResult>,
  delay: number = 300
) {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (data: T): Promise<ValidationResult> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await validator(data);
        resolve(result);
      }, delay);
    });
  };
}